/** Presentation model for the customer-portrait prototype. No patient/API data. */
export const PREVIEW_AS_OF = "2026-10-08";
export const AGE_BANDS = ["Under 30", "30–39", "40–49", "50–59", "60+", "Ukjent"];
export const MEMBERSHIPS = ["Kun privat", "Kun bedrift", "Begge", "Ingen"];
export const GENDERS = ["Kvinne", "Mann", "Ukjent"];
export const TIERS = ["Årlig", "Halvårlig", "Kvartalsvis", "Annet / ukjent"];

// Option labels from backend/src/db/seeds/surveyData.ts. Free text is never displayed.
export const TOPICS = {
  goals: {
    category: "Helsemål",
    question: "Hva er dine viktigste helsemål?",
    options: [
      "Bedre søvn", "Økt utholdenhet og kondisjon", "Forebygge og redusere risiko for sykdom",
      "Bli gravid", "Gå ned i vekt", "Bygge muskler", "Redusere stress", "Optimalisere egen helse",
      "Få en best mulig overgangsalder (kun for kvinner)", "Oppfølging under graviditet",
      "Få økt energi og overskudd", "Hormonbalanse", "Bedre hjertehelse", "Annet",
    ],
  },
  challenges: {
    category: "Helseutfordringer",
    question: "Har du noen helseutfordringer i dag som Aminos lege bør vite om?",
    options: [
      "Ingen symptomer eller plager. Føler meg i fin form.", "Nedsatt libido/seksuallyst",
      "Sliter med å bli gravid", "Uønsket vekttap", "Uønsket vektøkning", "Dårlig eller lite søvn",
      "Har lite energi eller er mye sliten og trøtt", "Annet",
    ],
  },
} as const;
export type Topic = keyof typeof TOPICS;
export type Person = {
  id: number;
  status: "active" | "churned" | "lead";
  entryDate: string;
  birthDate: string | null;
  gender: string;
  company: boolean;
  private: boolean;
  tier: string | null;
  wearableConnected: boolean;
  wearableData: boolean;
  selfReported: boolean;
  bmi: number | null;
  firstGap: number | null;
  latestGap: number | null;
  gapMonths: number | null;
  answers: Record<Topic, string[] | null>;
  entryAnswers: Record<Topic, string[] | null>;
};
export type PortraitFilters = {
  population: string;
  relationship: string;
  tier: string;
  gender: string;
  age: string;
  ageBasis: string;
};
export type BreakdownRow = { label: string; count: number; denominator: number; percent: number };

export function ageAt(birthDate: string | null, asOf: string): number | null {
  if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate) || !/^\d{4}-\d{2}-\d{2}$/.test(asOf)) return null;
  const birth = new Date(`${birthDate}T00:00:00Z`);
  const end = new Date(`${asOf}T00:00:00Z`);
  if (!Number.isFinite(birth.getTime()) || !Number.isFinite(end.getTime())) return null;
  if (birth.toISOString().slice(0, 10) !== birthDate || end.toISOString().slice(0, 10) !== asOf) return null;
  const beforeBirthday = end.getUTCMonth() < birth.getUTCMonth() ||
    (end.getUTCMonth() === birth.getUTCMonth() && end.getUTCDate() < birth.getUTCDate());
  const age = end.getUTCFullYear() - birth.getUTCFullYear() - Number(beforeBirthday);
  return age >= 0 && age < 130 ? age : null;
}
export function ageBand(age: number | null): string {
  return age === null ? "Ukjent" : age < 30 ? "Under 30" : age < 40 ? "30–39" :
    age < 50 ? "40–49" : age < 60 ? "50–59" : "60+";
}
export function personAgeBand(person: Person, basis: string): string {
  return ageBand(ageAt(person.birthDate, basis === "entry" ? person.entryDate : PREVIEW_AS_OF));
}
export function membership(person: Person): string {
  return person.private && person.company ? "Begge" : person.private ? "Kun privat" :
    person.company ? "Kun bedrift" : "Ingen";
}
export function filterPeople(people: readonly Person[], filters: PortraitFilters): Person[] {
  return people.filter((p) =>
    (!filters.population || p.status === filters.population) &&
    (!filters.relationship || (filters.relationship === "private" ? p.private :
      filters.relationship === "company" ? p.company : p.private && p.company)) &&
    (!filters.tier || (p.private && p.tier === filters.tier)) &&
    (!filters.gender || p.gender === filters.gender) &&
    (!filters.age || personAgeBand(p, filters.ageBasis) === filters.age));
}
export function share(count: number, denominator: number): number {
  return denominator ? count / denominator * 100 : 0;
}
export function hasTopicAnswer(answers: string[] | null): boolean {
  return answers?.some((answer) => answer.trim().length > 0) ?? false;
}
/** Boolean evidence per person: multiple answers, visits or wearable sources never inflate coverage. */
export function portraitIndicators(people: readonly Person[]) {
  const distinctCount = (matches: (p: Person) => boolean) => new Set(people.filter(matches).map((p) => p.id)).size;
  return {
    total: distinctCount(() => true),
    answered: distinctCount((p) => hasTopicAnswer(p.answers.goals)),
    wearables: distinctCount((p) => p.wearableConnected || p.wearableData),
    selfReported: distinctCount((p) => p.selfReported),
  };
}
export function breakdown(people: readonly Person[], labels: readonly string[], value: (p: Person) => string): BreakdownRow[] {
  return labels.map((label) => {
    const count = people.filter((p) => value(p) === label).length;
    return { label, count, denominator: people.length, percent: share(count, people.length) };
  });
}
export function topicRows(people: readonly Person[], topic: Topic, baseline = false): BreakdownRow[] {
  const answers = (p: Person) => (baseline ? p.entryAnswers : p.answers)[topic];
  const denominator = people.filter((p) => hasTopicAnswer(answers(p))).length;
  return TOPICS[topic].options.map((label) => {
    const count = people.filter((p) => answers(p)?.includes(label)).length;
    return { label, count, denominator, percent: share(count, denominator) };
  });
}
export function normalizeChoices(response: string | null, options: readonly string[]): string[] | null {
  if (!response?.trim()) return null;
  const choices = [...new Set(response.split(";").map((v) => v.trim()).filter(Boolean).map((token) => {
    return token.startsWith("freetext:") || !options.includes(token) ? "Annet" : token;
  }))];
  return choices.length ? choices : null;
}
export function comparisonGroups(people: readonly Person[], dimension: string, ageBasis: string) {
  const labels = dimension === "gender" ? GENDERS : dimension === "membership" ? MEMBERSHIPS : AGE_BANDS;
  return labels.map((label) => ({
    label,
    people: people.filter((p) => (dimension === "gender" ? p.gender :
      dimension === "membership" ? membership(p) : personAgeBand(p, ageBasis)) === label),
  }));
}
export function monthRange(from: string, to: string): string[] {
  if (!/^\d{4}-\d{2}$/.test(from) || !/^\d{4}-\d{2}$/.test(to)) return [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (!fy || !ty || !fm || !tm || fm > 12 || tm > 12) return [];
  const start = fy * 12 + fm - 1;
  const end = ty * 12 + tm - 1;
  if (end < start || end - start > 60) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => {
    const month = start + i;
    return `${Math.floor(month / 12)}-${String(month % 12 + 1).padStart(2, "0")}`;
  });
}
export function formatPortraitMonth(month: string, short = false): string {
  if (!monthRange(month, month).length) return month;
  const date = new Date(`${month}-01T00:00:00Z`);
  const label = date.toLocaleDateString("nb-NO", {
    month: short ? "short" : "long", year: short ? "2-digit" : "numeric", timeZone: "UTC",
  });
  return label.charAt(0).toLocaleUpperCase("nb-NO") + label.slice(1);
}
export function pairedGaps(people: readonly Person[]) {
  const paired = people.filter((p) => p.firstGap !== null && p.latestGap !== null && (p.gapMonths ?? 0) > 0);
  const mean = (value: (p: Person) => number) => paired.length ?
    paired.reduce((sum, p) => sum + value(p), 0) / paired.length : null;
  return {
    count: paired.length,
    first: mean((p) => p.firstGap!),
    latest: mean((p) => p.latestGap!),
    change: mean((p) => p.latestGap! - p.firstGap!),
    months: mean((p) => p.gapMonths!),
  };
}
